import {
  type DeleteWorkspaceLayoutPresetMutation,
  type DeleteWorkspaceLayoutPresetMutationVariables,
  type WorkspaceLayoutPreset,
} from 'generated/graphql';
import { useState, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import { useMutation } from '@apollo/client';

import { useWorkspaceLayoutManager } from '../useWorkspaceLayoutManager';
import Text from 'common/ui/Text';
import { Stack } from 'common/ui/Layout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddIcon from '@mui/icons-material/Add';

import { Colors } from 'styles/index';
import { transparentize } from 'color2k';
import { WorkspaceLayoutMenuSection } from './WorkspaceLayoutMenuSection';
import { WorkspaceLayoutModal } from './WorkspaceLayoutModal';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { telemetryContextFor } from 'modules/Apollo/types';
import { DELETE_WORKSPACE_LAYOUT_PRESET, GET_ME } from 'modules/Apollo/queries';
import { NameSpaces, CrossWindowDataRefetchType, sendCrossTabEvent } from 'modules/EventsManager';
import assocPath from 'ramda/es/assocPath';
import { useRecoilValue } from 'recoil';
import { workspaceDisplayArrangementState } from '../state';
import { sortWorkspaceLayoutPresets } from '../utils';

const CaptureIcon = () => (
  <Stack
    alignY="center"
    alignX="center"
    css={{
      width: 32,
      height: 32,
      borderRadius: 4,
      border: `2px dashed ${Colors.blue4}`,
      flexShrink: 0,
    }}
  >
    <AddIcon
      sx={{
        color: Colors.blue4,
        fontSize: 16,
      }}
    />
  </Stack>
);

type WorkspaceLayoutModalState = {
  mode: 'create' | 'edit' | 'clone';
  workspaceLayoutPreset: Partial<WorkspaceLayoutPreset> | null;
  isOpen: boolean;
};

type WorkspaceLayoutMenuProps = {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
};

const WorkspaceLayoutMenu = ({ isOpen, anchorEl, onClose }: WorkspaceLayoutMenuProps) => {
  const { data: user } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const me = user?.me;

  const notifyWorkspaceLayoutPresetsChanged = useCallback(() => {
    sendCrossTabEvent(NameSpaces.CROSS_WINDOW_DATA_REFETCH, {
      type: CrossWindowDataRefetchType.USER,
    });
  }, []);
  const currentDisplaysArrangement = useRecoilValue(workspaceDisplayArrangementState);
  const currentDisplays = useMemo(
    () => currentDisplaysArrangement?.displays ?? [],
    [currentDisplaysArrangement]
  );

  const workspaceLayoutPresets = useMemo(() => {
    const presets = [...(me?.workspaceSettings?.layoutSettings?.presets ?? [])];

    return sortWorkspaceLayoutPresets(presets, currentDisplays);
  }, [me, currentDisplays]);

  const [modalState, setModalState] = useState<WorkspaceLayoutModalState>({
    mode: 'create',
    workspaceLayoutPreset: null,
    isOpen: false,
  });

  const { captureWorkspaceLayoutPreset } = useWorkspaceLayoutManager();
  const [deleteWorkspaceLayoutPreset] = useMutation<
    DeleteWorkspaceLayoutPresetMutation,
    DeleteWorkspaceLayoutPresetMutationVariables
  >(DELETE_WORKSPACE_LAYOUT_PRESET, {
    context: APOLLO_QUERY_CONTEXT,
  });

  const captureAndDisplayLayoutPreset = useCallback(async () => {
    const windowsLayout = await captureWorkspaceLayoutPreset();
    setModalState({
      mode: 'create',
      workspaceLayoutPreset: { ...windowsLayout },
      isOpen: true,
    });
  }, [captureWorkspaceLayoutPreset]);

  const handleEditWorkspaceLayoutPreset = useCallback(
    (workspaceLayoutPreset: Partial<WorkspaceLayoutPreset>) => {
      setModalState({
        mode: 'edit',
        workspaceLayoutPreset,
        isOpen: true,
      });
    },
    []
  );

  const handleCloneWorkspaceLayoutPreset = useCallback(
    (workspaceLayoutPreset: Partial<WorkspaceLayoutPreset>) => {
      setModalState({
        mode: 'clone',
        workspaceLayoutPreset,
        isOpen: true,
      });
    },
    []
  );

  const handleDeleteWorkspaceLayoutPreset = useCallback(
    async ({ id }: Partial<WorkspaceLayoutPreset>) => {
      if (id == null) {
        return;
      }

      await deleteWorkspaceLayoutPreset({
        variables: { id },
        update: (cache, { data }) => {
          const deletedId = data?.deleteWorkspaceLayoutPreset?.id;
          if (deletedId == null || user == null) {
            return;
          }

          const filteredPresets =
            user.me?.workspaceSettings?.layoutSettings?.presets?.filter(
              (preset) => preset.id !== deletedId
            ) ?? [];
          cache.writeQuery({
            query: GET_ME,
            data: assocPath(
              ['me', 'workspaceSettings', 'layoutSettings', 'presets'],
              filteredPresets,
              user
            ),
          });
        },
        onCompleted: () => {
          notifyWorkspaceLayoutPresetsChanged();
          setModalState((prevModalState) =>
            prevModalState.workspaceLayoutPreset?.id === id
              ? { ...prevModalState, isOpen: false }
              : prevModalState
          );
        },
      });
    },
    [deleteWorkspaceLayoutPreset, notifyWorkspaceLayoutPresetsChanged, user]
  );

  return (
    <>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          root: {
            slotProps: {
              backdrop: {
                sx: {
                  backgroundColor: 'rgba(0, 0, 0, 0.28)',
                },
              },
            },
          },
          paper: {
            sx: {
              width: 400,
              maxHeight: 650,
              overflowY: 'hidden',
            },
          },
        }}
      >
        <DialogTitle sx={{ p: 2.5 }}>
          <Stack alignY="center" space="xsmall">
            <DashboardIcon
              css={`
                color: ${Colors.gray7};
              `}
            />
            <Text>Workspace Layouts</Text>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <Box
            sx={{
              px: 2.5,
              pb: 2.5,
              maxHeight: 540,
              overflowY: 'auto',
              mr: -1,
              pr: 1.5,
            }}
          >
            <Box sx={{ pr: 3 }}>
              <ListItemButton
                onClick={captureAndDisplayLayoutPreset}
                sx={{
                  mb: 2,
                  padding: 2,
                  borderRadius: 2,
                  border: `1px solid ${transparentize(Colors.gray5, 0.5)}`,
                  backgroundColor: transparentize(Colors.gray7, 0.9),
                  transition: 'all 150ms ease',

                  '&:hover': {
                    backgroundColor: transparentize(Colors.gray4, 0.1),
                  },
                }}
              >
                <Stack space="small">
                  <CaptureIcon />
                  <ListItemText
                    primary="Capture Current Layout"
                    secondary="Save the current arrangement as a new layout"
                    slotProps={{
                      primary: {
                        fontWeight: 500,
                        fontSize: 12,
                      },
                      secondary: {
                        mt: 0.25,
                        lineHeight: 1.35,
                        fontSize: 12,
                      },
                    }}
                  />
                </Stack>
              </ListItemButton>
            </Box>
            <WorkspaceLayoutMenuSection
              title="Saved Layouts"
              workspaceLayoutPresets={workspaceLayoutPresets}
              onEdit={handleEditWorkspaceLayoutPreset}
              onClone={handleCloneWorkspaceLayoutPreset}
              onDelete={handleDeleteWorkspaceLayoutPreset}
            />
          </Box>
        </DialogContent>
      </Popover>
      {modalState.isOpen && (
        <WorkspaceLayoutModal
          mode={modalState.mode}
          open={modalState.isOpen}
          onClose={() => {
            setModalState({ ...modalState, isOpen: false });
          }}
          onDelete={
            modalState.mode === 'edit'
              ? () => handleDeleteWorkspaceLayoutPreset(modalState.workspaceLayoutPreset ?? {})
              : undefined
          }
          workspaceLayoutPreset={modalState.workspaceLayoutPreset as WorkspaceLayoutPreset}
        />
      )}
    </>
  );
};

WorkspaceLayoutMenu.displayName = 'WorkspaceLayoutMenu';

export default WorkspaceLayoutMenu;

const APOLLO_QUERY_CONTEXT = telemetryContextFor(WorkspaceLayoutMenu);
